import React, { useCallback, useEffect, useRef, useState } from "react";

import { setCurrentReceivedUrl } from "@/common/currentPageUrl";
import { decodeLineState } from "@/common/utils";
import { AgentSchema as AgentSchemaV1 } from "@/v1/types/client";
import { ParentSiteStateType } from "@/v1/utils/atoms";
import "@/v1/recoGlobal.css";
import "@/v1/recoReset.css";
import "@/v1/theme.scss";
import "@/v1/common.scss";
import { sendFrameState } from "@/v2/hooks/useFrameControl";
import { getAgent } from "@/v2/libs/apiClient";
import {
  applyLocalPreviewAgent,
  createLocalPreviewAgent,
  shouldForceLocalPreview,
  shouldUseLocalPreview,
} from "@/v2/libs/localPreview";
import {
  loadLocalPreviewAgentSettings,
  loadRemoteLocalPreviewAgentSettings,
} from "@/v2/libs/localPreviewSettings";
import { AgentSchema } from "@/v2/types/client";
import Loading from "@/v2/utils/Loading";
import {
  AGENT_VERSION,
  LINE_STATE_KEY,
  MESSAGE_TYPE,
  PARENT_ORIGIN_KEY,
  SOURCE_IFRAME,
  SOURCE_PARENT,
} from "@/v2/utils/constants";
import "@/v2/recoGlobal.css";
import "@/v2/recoReset.css";
import "@/v2/common.scss";

// Keep PARENT_ORIGIN_KEY fresh so mobile LINE login always has a URL to return to

// Lazy load version-specific AppInitializers. CSS is imported statically above
// so local Vite does not have to dynamically import CSS entry modules.
// eslint-disable-next-line import/extensions
const AppInitializerStudio = React.lazy(() => import("@/v1/AppInitializer"));
// eslint-disable-next-line import/extensions
const AppInitializerNonStudio = React.lazy(() => import("@/v2/AppInitializer"));

/** Delay in ms to allow lazy-loaded CSS to be applied before rendering. */
const CSS_LOAD_DELAY_MS = 100;
const LOCAL_PREVIEW_AGENT_ID = "00000000-0000-4000-8000-000000000001";
const LOCAL_PREVIEW_REFRESH_MS = 500;
const AGENT_CACHE_PREFIX = "pitaliy_cached_agent:v2-settings-20260612-live-design:";
const AGENT_CACHE_TTL_MS = 2 * 60 * 1000;
const MAX_AGENT_FETCH_RETRIES = 3;

const shouldApplyLocalPreviewToAgent = (agentId: string) =>
  shouldUseLocalPreview() &&
  Boolean(agentId) &&
  (agentId === LOCAL_PREVIEW_AGENT_ID || shouldForceLocalPreview());

const isAdminPreviewRequest = () => {
  if (typeof window === "undefined") return false;
  if (window.self === window.top) return false;
  const preview = new URLSearchParams(window.location.search).get("preview");
  return Boolean(preview && preview.startsWith("admin-"));
};

const getStandaloneAgentId = () => {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const queryAgentId = params.get("agent_id") ?? params.get("agentId");
  if (queryAgentId) return queryAgentId;

  return import.meta.env.DEV ? import.meta.env.VITE_LOCAL_AGENT_ID ?? "" : "";
};

const getCachedAgent = (targetAgentId: string) => {
  if (import.meta.env.DEV) return null;
  if (typeof window === "undefined" || !targetAgentId) return null;

  try {
    const raw = window.sessionStorage.getItem(
      `${AGENT_CACHE_PREFIX}${targetAgentId}`,
    );
    if (!raw) return null;
    const payload = JSON.parse(raw) as {
      cachedAt?: number;
      agent?: AgentSchema | AgentSchemaV1;
    };
    if (!payload.cachedAt || !payload.agent) return null;
    if (Date.now() - payload.cachedAt > AGENT_CACHE_TTL_MS) return null;
    return payload.agent;
  } catch {
    return null;
  }
};

const cacheAgent = (
  targetAgentId: string,
  nextAgent: AgentSchema | AgentSchemaV1,
) => {
  if (import.meta.env.DEV) return;
  if (typeof window === "undefined" || !targetAgentId) return;

  try {
    window.sessionStorage.setItem(
      `${AGENT_CACHE_PREFIX}${targetAgentId}`,
      JSON.stringify({
        cachedAt: Date.now(),
        agent: nextAgent,
      }),
    );
  } catch {
    // Cache is best effort only. Never block the widget.
  }
};

type EmbedStartMode = "standby" | "direct";

/**
 * Root AppInitializer that routes to v1 or v2 based on agent version
 * - Receives messages from pitaliy.js
 * - Fetches agent data and determines version
 * - Renders appropriate version component
 */
function AppInitializer() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [version, setVersion] = useState<AGENT_VERSION | null>(null);
  const [cssLoaded, setCssLoaded] = useState<boolean>(false);
  const [agentId, setAgentId] = useState<string>(() => getStandaloneAgentId());
  const [agent, setAgent] = useState<AgentSchema | AgentSchemaV1 | null>(null);
  const [receivedUrl, setReceivedUrl] = useState<string>(() =>
    getStandaloneAgentId() ? window.location.href : "",
  );
  const [parentSiteState, setParentSiteState] = useState<ParentSiteStateType>(
    {},
  );
  const [parentWidth, setParentWidth] = useState<number>(() =>
    getStandaloneAgentId() ? window.innerWidth : 0,
  );
  const [embedStartMode, setEmbedStartMode] =
    useState<EmbedStartMode>("standby");
  const [lineOAuthCallback, setLineOAuthCallback] = useState<{
    code: string;
    state: string;
  } | null>(null);
  const agentFetchRetryCountRef = useRef(0);
  const agentFetchRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Handle messages from pitaliy.js
  const handleMessage = useCallback((event: MessageEvent) => {
    if (typeof event.data !== "object") return;

    switch (event.data.type) {
      case MESSAGE_TYPE.HANDSHAKE_REQUEST:
        if (event.data.source === SOURCE_PARENT) {
          // Send handshake response to confirm iframe is ready
          sendFrameState({
            type: MESSAGE_TYPE.HANDSHAKE_RESPONSE,
            timestamp: Date.now(),
          });
        }
        break;
      case MESSAGE_TYPE.SET_URL:
        if (event.data.url) {
          setReceivedUrl(event.data.url);
          // Persist the parent page URL so mobile LINE login can redirect back
          // after LINE auth completes on b.com standalone (no postMessage there).
          localStorage.setItem(PARENT_ORIGIN_KEY, event.data.url);
          setCurrentReceivedUrl(event.data.url);
        }
        break;
      case MESSAGE_TYPE.AGENT_ID:
        if (event.data.agentId) {
          setAgentId(event.data.agentId);
        }
        if (event.data.startMode === "direct") {
          setEmbedStartMode("direct");
        } else if (event.data.startMode === "standby") {
          setEmbedStartMode("standby");
        }
        if (event.data.parentWidth) {
          setParentWidth(event.data.parentWidth);
        }
        break;
      case MESSAGE_TYPE.LINE_OAUTH_CALLBACK:
        if (
          event.data.source === SOURCE_PARENT &&
          event.data.code &&
          event.data.state
        ) {
          setLineOAuthCallback({
            code: event.data.code,
            state: event.data.state,
          });
        }
        break;
      default:
        if (event.data.source === SOURCE_PARENT && event.data.state) {
          setParentSiteState(event.data.state);
        }
        break;
    }
  }, []);

  // Fetch agent and determine version, then load CSS
  const fetchAgentAndVersion = useCallback(async () => {
    if (!agentId) {
      // Keep loading state until agentId is received
      return;
    }

    try {
      const shouldUseLocalPreviewForAgent =
        shouldApplyLocalPreviewToAgent(agentId);
      const shouldUseAdminPreviewOnly =
        shouldUseLocalPreviewForAgent && isAdminPreviewRequest();
      const shouldUseLocalOnly =
        shouldUseAdminPreviewOnly ||
        (shouldForceLocalPreview() && agentId === LOCAL_PREVIEW_AGENT_ID);
      const fetchedAgentData = shouldUseLocalOnly
        ? createLocalPreviewAgent(agentId)
        : await getAgent({ agent_id: agentId });
      const remoteLocalSettings = shouldUseLocalPreviewForAgent
        ? await loadRemoteLocalPreviewAgentSettings(agentId)
        : {};
      const agentData = shouldUseLocalPreviewForAgent
        ? applyLocalPreviewAgent(fetchedAgentData, agentId, remoteLocalSettings)
        : fetchedAgentData;
      if (!agentData) {
        throw new Error("Agent data is not available.");
      }
      agentFetchRetryCountRef.current = 0;
      // Determine version from agent data, default to Studio if not specified
      const agentVersion = agentData.version || AGENT_VERSION.Studio;
      setVersion(agentVersion);
      setAgent(agentData);
      cacheAgent(agentId, agentData);

      // CSS will be loaded by React.lazy; short delay so it is applied before render
      await new Promise<void>((resolve) => {
        setTimeout(resolve, CSS_LOAD_DELAY_MS);
      });
      setCssLoaded(true);

      // Send version to parent
      window.parent.postMessage(
        {
          source: SOURCE_IFRAME,
          type: MESSAGE_TYPE.AGENT_VERSION,
          version:
            agentVersion === AGENT_VERSION.Studio
              ? AGENT_VERSION.Studio
              : AGENT_VERSION.NonStudio,
        },
        "*",
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("AppInitializer: Failed to fetch agent data:", error);
      if (shouldUseLocalPreview() && agentId === LOCAL_PREVIEW_AGENT_ID) {
        const remoteLocalSettings =
          await loadRemoteLocalPreviewAgentSettings(agentId);
        const agentData = applyLocalPreviewAgent(
          createLocalPreviewAgent(agentId),
          agentId,
          remoteLocalSettings,
        );
        setVersion(agentData.version || AGENT_VERSION.NonStudio);
        setAgent(agentData);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, CSS_LOAD_DELAY_MS);
        });
        setCssLoaded(true);
        return;
      }

      const cachedAgent = getCachedAgent(agentId);
      if (cachedAgent) {
        setVersion(cachedAgent.version || AGENT_VERSION.Studio);
        setAgent(cachedAgent);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, CSS_LOAD_DELAY_MS);
        });
        setCssLoaded(true);
        return;
      }

      if (agentFetchRetryCountRef.current < MAX_AGENT_FETCH_RETRIES) {
        agentFetchRetryCountRef.current += 1;
        if (agentFetchRetryTimerRef.current) {
          clearTimeout(agentFetchRetryTimerRef.current);
        }
        agentFetchRetryTimerRef.current = setTimeout(() => {
          setIsLoading(true);
          void fetchAgentAndVersion();
        }, 800 * agentFetchRetryCountRef.current);
        return;
      }
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  // Listen for messages from pitaliy.js
  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      if (agentFetchRetryTimerRef.current) {
        clearTimeout(agentFetchRetryTimerRef.current);
      }
    };
  }, [handleMessage]);

  useEffect(() => {
    if (typeof window === "undefined" || window.self !== window.top) {
      return undefined;
    }

    const updateStandaloneParentWidth = () => {
      setParentWidth(window.innerWidth);
    };

    updateStandaloneParentWidth();
    window.addEventListener("resize", updateStandaloneParentWidth);
    return () => {
      window.removeEventListener("resize", updateStandaloneParentWidth);
    };
  }, []);

  // On mobile, LINE redirects back to b.com standalone. Parent URL and state
  // are encoded in the OAuth state param so we don't rely on localStorage
  // (which can be partitioned when b.com was loaded in an iframe).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const returnedState = params.get("state");

    if (!code || !returnedState) return;

    const payload = decodeLineState(returnedState);
    if (payload && payload.p) {
      const parentUrl = new URL(payload.p);
      parentUrl.searchParams.set("code", code);
      parentUrl.searchParams.set("state", returnedState);
      window.location.href = parentUrl.toString();
      return;
    }

    const savedState = localStorage.getItem(LINE_STATE_KEY);
    if (returnedState === savedState) {
      const pUrl = localStorage.getItem(PARENT_ORIGIN_KEY);
      if (pUrl) {
        const parentUrl = new URL(pUrl);
        parentUrl.searchParams.set("code", code);
        parentUrl.searchParams.set("state", returnedState);
        window.location.href = parentUrl.toString();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch agent when agentId is available
  useEffect(() => {
    fetchAgentAndVersion();
  }, [fetchAgentAndVersion]);

  useEffect(() => {
    if (
      !shouldApplyLocalPreviewToAgent(agentId) ||
      !agentId ||
      version !== AGENT_VERSION.NonStudio
    ) {
      return undefined;
    }

    let lastSignature = "";
    let isRefreshing = false;
    let isDisposed = false;
    const readLocalPreviewSignature = async () => {
      const [cookieSettings, remoteSettings] = await Promise.all([
        Promise.resolve(loadLocalPreviewAgentSettings()),
        loadRemoteLocalPreviewAgentSettings(agentId),
      ]);
      return {
        signature: JSON.stringify({ cookieSettings, remoteSettings }),
        remoteSettings,
      };
    };
    const refreshLocalPreviewAgent = async () => {
      if (isRefreshing) return;
      isRefreshing = true;
      const { signature: nextSignature, remoteSettings } =
        await readLocalPreviewSignature().finally(() => {
          isRefreshing = false;
        });
      if (isDisposed || nextSignature === lastSignature) return;
      lastSignature = nextSignature;
      setAgent((current) =>
        current
          ? applyLocalPreviewAgent(
              current as AgentSchema,
              agentId,
              remoteSettings,
            )
          : current,
      );
    };

    void refreshLocalPreviewAgent();

    const intervalId = window.setInterval(() => {
      void refreshLocalPreviewAgent();
    }, LOCAL_PREVIEW_REFRESH_MS);
    window.addEventListener("focus", refreshLocalPreviewAgent);
    document.addEventListener("visibilitychange", refreshLocalPreviewAgent);

    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshLocalPreviewAgent);
      document.removeEventListener(
        "visibilitychange",
        refreshLocalPreviewAgent,
      );
    };
  }, [agentId, version]);

  // Show loading if any required data is missing
  if (!agentId || isLoading) {
    return <Loading className="w-screen h-screen" />;
  }

  // Show loading if version/agent not loaded yet or CSS not ready
  if (!version || !agent || !cssLoaded) {
    return <Loading className="w-screen h-screen" />;
  }

  // Route to appropriate version
  return (
    <React.Suspense fallback={<Loading className="w-screen h-screen" />}>
      {version === AGENT_VERSION.Studio ? (
        <AppInitializerStudio
          agentId={agentId}
          agent={agent as AgentSchemaV1}
          receivedUrl={receivedUrl}
          parentSiteState={parentSiteState}
          embedStartMode={embedStartMode}
          lineOAuthCallback={lineOAuthCallback}
          onLineOAuthCallbackHandled={() => setLineOAuthCallback(null)}
        />
      ) : (
        <AppInitializerNonStudio
          agentId={agentId}
          parentWidth={parentWidth}
          agent={agent as AgentSchema}
          receivedUrl={receivedUrl}
          embedStartMode={embedStartMode}
          lineOAuthCallback={lineOAuthCallback}
          onLineOAuthCallbackHandled={() => setLineOAuthCallback(null)}
        />
      )}
    </React.Suspense>
  );
}

export default AppInitializer;
